/**
 * ----------------------------------------------------------------------------------------------------------------
 * KEYBOARDNAV - ACCESSIBILITY FIXES
 * ----------------------------------------------------------------------------------------------------------------
 *
 * One place for remediations that patch third-party or theme markup we cannot edit at the source.
 *
 * Each fix is a method on `fixes` below. To add one: write a new method, then list its name in `ENABLED`. Every fix
 * must be safe to run twice (guard with a data attribute) since scripts here may run against markup that is injected
 * and re-injected at runtime.
 */
(() => {
  'use strict';


  /**
   * Enter on a checkbox toggles it, e.g. enterTogglesCheckbox('.globo-formbuilder'). Natively Enter does not touch a
   * checkbox at all: it submits the form the checkbox sits in, which sends people who reach for it half-filled forms.
   * Space still toggles as usual.
   *
   * Pass a container selector to scope this; with none, it applies to every checkbox on the page. Scoping matters
   * because the submit this replaces is legitimate behaviour elsewhere.
   *
   * Delegated from the document, so checkboxes rendered later are covered without watching for them.
   */
  const enterTogglesCheckbox = (selector = null) => {
    document.addEventListener('keydown', (event) => {
      if (event.key !== 'Enter') return;

      const checkbox = event.target;
      if (!(checkbox instanceof HTMLInputElement) || checkbox.type !== 'checkbox') return;
      if (selector && !checkbox.closest(selector)) return;

      // Without this the form submits instead, and the click below never lands.
      event.preventDefault();

      // click() rather than flipping .checked, so the app's own change / click handlers run as they would on a tap.
      checkbox.click();
    });
  };
  enterTogglesCheckbox();

  /**
   * Shared helpers. Kept out of `fixes` so any fix can reach them.
   */

  let idSequence = 0;

  /** A document-unique id, for elements that need one before aria-labelledby and friends can point at them. */
  const nextId = (prefix) => {
    let id;
    do {
      id = `${prefix}-${++idSequence}`;
    } while (document.getElementById(id));
    return id;
  };

  /** Rendered and not hidden. The box check covers display:none and detached nodes, which have no boxes at all. */
  const isVisible = (element) =>
    Boolean(element.offsetWidth || element.offsetHeight || element.getClientRects().length) &&
    getComputedStyle(element).visibility !== 'hidden';

  const FOCUSABLE = [
    'a[href]',
    'button',
    'input',
    'select',
    'textarea',
    'details > summary',
    'iframe',
    'audio[controls]',
    'video[controls]',
    '[contenteditable]',
    '[tabindex]'
  ].join(',');

  /** Focusable descendants in tab order, minus anything disabled, hidden, or taken out of the order. */
  const focusableWithin = (root) =>
    Array.from(root.querySelectorAll(FOCUSABLE)).filter(
      (element) =>
        !element.hasAttribute('disabled') &&
        element.getAttribute('aria-hidden') !== 'true' &&
        element.tabIndex >= 0 &&
        isVisible(element)
    );

  const fixes = {
    /**
     * The Effective Apps terms-and-conditions app (app.tncapp.com) renders its modal with tingle.js, which ships no
     * dialog semantics and no focus management: the overlay is a plain <div>, nothing labels it, focus stays behind on
     * the page, and Tab walks straight out of the modal into the page underneath.
     *
     * This adds, per modal:
     *   - role="dialog" + aria-modal="true"
     *   - aria-labelledby pointing at the modal's own heading (an id is generated when the heading has none)
     *   - a focus trap that cycles Tab / Shift+Tab within the modal
     *   - Escape to close
     *   - focus returned to whatever element opened the modal
     *
     * role="dialog" goes on `.tingle-modal` rather than the inner `.tingle-modal-box` because tingle renders its close
     * button as a sibling of the box. Labelling the box instead would leave the close button outside the dialog, where
     * aria-modal="true" hides it from assistive tech.
     */
    tingleModal() {
      const MODAL = '.tingle-modal';
      const VISIBLE = 'tingle-modal--visible';
      const FALLBACK_LABEL = 'Dialog';

      // Tracks the last element focused outside any modal, so we know where to send focus back to on close.
      let lastFocusedOutside = null;
      document.addEventListener('focusin', (event) => {
        const element = event.target;
        if (element instanceof HTMLElement && !element.closest(MODAL)) lastFocusedOutside = element;
      });

      const active = new WeakMap();

      /** Static, one-time semantics. Separate from activate() because these outlive any single open/close cycle. */
      const enhance = (modal) => {
        if (modal.dataset.knavEnhanced) return;
        modal.dataset.knavEnhanced = 'true';

        modal.setAttribute('role', 'dialog');
        modal.setAttribute('aria-modal', 'true');
        if (!modal.hasAttribute('tabindex')) modal.setAttribute('tabindex', '-1');

        // Prefer the h1. Fall back through the other headings so a modal authored with an h2 is still labelled.
        const heading = modal.querySelector('h1') || modal.querySelector('h2, h3, h4, h5, h6');

        if (heading) {
          if (!heading.id) heading.id = nextId('knav-modal-title');
          modal.setAttribute('aria-labelledby', heading.id);
        } else if (!modal.hasAttribute('aria-label') && !modal.hasAttribute('aria-labelledby')) {
          modal.setAttribute('aria-label', FALLBACK_LABEL);
        }

        // tingle hides the close button's text label at some breakpoints, which can leave it with no accessible name.
        const closeButton = modal.querySelector('.tingle-modal__close');
        if (closeButton && !closeButton.hasAttribute('aria-label')) {
          closeButton.setAttribute('aria-label', 'Close');

          closeButton.addEventListener('keydown', (event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              close(modal);
            }
          })

        closeButton.addEventListener('click', (event) => {
            event.preventDefault();
              close(modal);
          })
        }
      };

      const close = (modal) => {
        // Going through tingle's own close button keeps the library's state (body class, callbacks) consistent.
        const closeButton = modal.querySelector('.tingle-modal__close');
        if (closeButton) {
          closeButton.click();
          document.querySelector('.shopify-payment-button__more-options')?.focus();
          return 
        }

        // Last resort when the modal was built without a close button: hide it and give the page its scroll back.
        modal.classList.remove(VISIBLE);
        document.body.classList.remove('tingle-enabled');
        deactivate(modal);
      };

      const onKeydown = (event) => {
        const modal = event.currentTarget;

        if (event.key === 'Escape') {
          event.preventDefault();
          close(modal);
          return;
        }

        if (event.key !== 'Tab') return;

        const focusables = focusableWithin(modal);

        // Nothing to tab to: hold focus on the dialog itself rather than letting it escape to the page behind.
        if (focusables.length === 0) {
          event.preventDefault();
          modal.focus();
          return;
        }

        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        const current = document.activeElement;

        if (event.shiftKey && (current === first || current === modal)) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && current === last) {
          event.preventDefault();
          first.focus();
        }
      };

      const activate = (modal) => {
        if (active.has(modal)) return;

        // Capture the opener before we move focus. activeElement can already be <body> if the opener was removed.
        const activeElement = document.activeElement;
        const opener =
          activeElement instanceof HTMLElement && activeElement !== document.body && !activeElement.closest(MODAL)
            ? activeElement
            : lastFocusedOutside;

        active.set(modal, { opener });
        modal.addEventListener('keydown', onKeydown);

        // Send focus into the dialog. The modal itself is the fallback so a modal with no controls is still reachable.
        // Deferred so it lands after tingle's own open transition, on both timers because requestAnimationFrame does
        // not fire in a background tab. Whichever runs first wins; the guard stops the second from stealing focus back.
        const target = focusableWithin(modal)[0] || modal;
        const focusTarget = () => {
          if (!active.has(modal) || modal.contains(document.activeElement)) return;
          target.focus();
        };
        requestAnimationFrame(focusTarget);
        setTimeout(focusTarget, 0);
      };

      const deactivate = (modal) => {
        const state = active.get(modal);
        if (!state) return;

        active.delete(modal);
        modal.removeEventListener('keydown', onKeydown);

        // Only restore if the opener is still in the document and still focusable.
        const { opener } = state;
        if (opener && opener.isConnected && isVisible(opener)) opener.focus();
      };

      const sync = (modal) => (modal.classList.contains(VISIBLE) ? activate(modal) : deactivate(modal));

      /** tingle toggles a class rather than removing the node, so each modal needs its own attribute observer. */
      const watch = (modal) => {
        enhance(modal);
        if (modal.dataset.knavWatched) return sync(modal);

        modal.dataset.knavWatched = 'true';
        new MutationObserver(() => sync(modal)).observe(modal, { attributeFilter: ['class'] });
        sync(modal);
      };

      const scan = (root) => {
        if (!(root instanceof HTMLElement)) return;
        if (root.matches(MODAL)) watch(root);
        root.querySelectorAll(MODAL).forEach(watch);
      };

      document.querySelectorAll(MODAL).forEach(watch);

      new MutationObserver((records) => {
        for (const record of records) {
          record.addedNodes.forEach(scan);

          // A destroyed modal never fires a class change, so restore focus on removal too.
          record.removedNodes.forEach((node) => {
            if (node instanceof HTMLElement && node.matches(MODAL)) deactivate(node);
          });
        }
      }).observe(document.body, { childList: true, subtree: true });
    },

    /**
     * "Buy with Shop Pay" and "More payment options" while the Effective Apps terms gate is up.
     *
     * The app gates those buttons by wrapping them in <a id="effectiveAppsBINWrapper"> — which opens the terms modal
     * — and setting pointer-events: none on the payment container so the real buttons never see the click. That only
     * stops a pointer. Two other ways of activating a button walk straight past it:
     *
     *   - Enter or Space. pointer-events has no bearing on the keyboard, so the buttons stay focusable and the key's
     *     synthetic click runs the payment button's own handler *and* bubbles to the wrapper — the modal opens and
     *     checkout starts underneath it.
     *   - A click that was dispatched rather than pointed. VoiceOver and the other screen readers activate a control
     *     by synthesising a click on it, and a synthesised click skips hit testing, so pointer-events never applies.
     *     Voice control and any script calling .click() reach the buttons the same way.
     *
     * Both are cancelled here and replayed on the wrapper, so every route to the button ends at the same modal a
     * mouse gets. It has to happen in the capture phase at the document: the Shop Pay button lives in a closed shadow
     * root, so its own listeners cannot be reached, and by the time the event bubbles back out to the wrapper they
     * have already run. Cancelling the default action is not enough on its own — those listeners start checkout
     * whether or not the default was cancelled, so the event has to be stopped before it ever reaches them.
     */
    paymentGateActivation() {
      const WRAPPER = '#effectiveAppsBINWrapper';

      // The app picks the event it gates on per theme, so the attribute it wrote is also what tells us the gate is
      // still up: accepting the terms removes the handler and restores pointer events.
      const GATE_EVENTS = ['click', 'pointerdown'];
      const gateEvent = (wrapper) => GATE_EVENTS.find((type) => wrapper.hasAttribute(`on${type}`)) || null;

      /** The wrapper this event should be handed to, or null when the event is none of our business. */
      const gatedWrapper = (event) => {
        const target = event.target;
        if (!(target instanceof HTMLElement)) return null;

        // Events from the shadow roots inside are retargeted to their host, which is still inside the wrapper. One on
        // the wrapper itself is either a real click or the one we replayed below, and has to be left alone.
        const wrapper = target.closest(WRAPPER);
        if (!wrapper || target === wrapper) return null;

        return gateEvent(wrapper) ? wrapper : null;
      };

      const intercept = (event, wrapper) => {
        event.preventDefault();
        event.stopImmediatePropagation();

        // Replay it on the wrapper, on whichever event the app bound its handler to.
        const type = gateEvent(wrapper);
        if (type === 'click') wrapper.click();
        else wrapper.dispatchEvent(new PointerEvent(type, { bubbles: true, cancelable: true }));
      };

      document.addEventListener(
        'keydown',
        (event) => {
          const isEnter = event.key === 'Enter';
          const isSpace = event.key === ' ' || event.key === 'Spacebar';
          if (!isEnter && !isSpace) return;

          // A modifier means the browser is being asked for something else (open in a new tab, and so on).
          if (event.altKey || event.ctrlKey || event.metaKey || event.shiftKey) return;

          const wrapper = gatedWrapper(event);
          if (!wrapper) return;

          // Space activates a button but only scrolls on a link, so leave the "More payment options" link's scroll be.
          if (isSpace && event.target.matches('a[href]')) return;

          intercept(event, wrapper);
        },
        true
      );

      // A real pointer cannot land on anything inside the wrapper while pointer-events: none is set, so a click that
      // reaches this was dispatched by assistive tech, voice control or a script. Cancelling it costs a mouse nothing.
      document.addEventListener(
        'click',
        (event) => {
          const wrapper = gatedWrapper(event);
          if (wrapper) intercept(event, wrapper);
        },
        true
      );
    },

    collectionLayoutSwitch() {
      const SWITCH = 'collection-layout-switch';
      const BUTTON = 'button[type="button"]';
      const ACTIVE = 'is-active';

      const sync = (element) => {
        element.querySelectorAll(BUTTON).forEach((button) => {
          button.setAttribute('aria-pressed', String(button.classList.contains(ACTIVE)));
        });
      };

      const watch = (element) => {
        if (element.dataset.knavLayoutSwitch) return sync(element);

        element.dataset.knavLayoutSwitch = 'true';

        // Only the class is watched, and only aria-pressed is written, so a sync cannot trigger another one.
        new MutationObserver(() => sync(element)).observe(element, { subtree: true, attributeFilter: ['class'] });
        sync(element);
      };

      const scan = (root) => {
        if (!(root instanceof HTMLElement)) return;
        if (root.matches(SWITCH)) watch(root);
        root.querySelectorAll(SWITCH).forEach(watch);
      };

      scan(document.body);

      // Filtering and sorting replace the whole toolbar through the Section Rendering API, so the switches that
      // arrive with it have to be picked up as well.
      new MutationObserver((records) => {
        for (const record of records) record.addedNodes.forEach(scan);
      }).observe(document.body, { childList: true, subtree: true });
    },

    /**
     * Essential Grid renders every tile with an invisible full-bleed <a class="eg-invisiblebutton"> inside
     * .esg-entry-cover. It duplicates the link the visible play button already provides, but carries no text, no
     * child nodes and no aria-label, so once the cover is revealed it puts an unnamed link into the tab order.
     *
     * This marks those duplicates as presentational and removes them from the tab order. The visible play button is
     * a sibling of the cover, not a child, so it is left untouched and remains the keyboard path to the video.
     *
     * The grid is injected by the app long after DOMContentLoaded, so we watch for it, then disconnect as soon as a
     * grid has been patched. TIMEOUT_MS stops us observing the document forever on pages that have no grid.
     */
    esgCoverLinks() {
      // This build of Essential Grid uses .esg-grid; .esg-container appears in other versions, so match both.
      const CONTAINERS = '.esg-container, .esg-grid';
      const COVER_LINK = '.esg-entry-cover > .esg-click-to-play-video';
      const TIMEOUT_MS = 20000;

      const patch = () => {
        let patched = 0;

        document.querySelectorAll(CONTAINERS).forEach((container) => {
          container.querySelectorAll(COVER_LINK).forEach((link, index) => {
            if (link.dataset.knavCoverLink) return;
            const currentNumber = ++index;
            link.dataset.knavCoverLink = 'true';
            link.setAttribute('tabindex', '0');
            link.setAttribute('aria-label', 'Play video ' + currentNumber);
            link.setAttribute('role', 'button');

            link.addEventListener('keydown', (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                link.click();
              }
            });
            setTimeout(() => {
              const parent = link.closest('.esg-media-cover-wrapper');
              if(parent) {
                parent.querySelector('iframe')?.setAttribute('aria-label', 'Video ' + currentNumber);
              }
            }, 0)

            patched++;
          });
        });

        return patched;
      };

      // The grid may already be present if this runs late.
      if (patch() > 0) return;

      const observer = new MutationObserver(() => {
        if (patch() > 0) {
          observer.disconnect();
          clearTimeout(timer);
        }
      });

      const timer = setTimeout(() => observer.disconnect(), TIMEOUT_MS);
      observer.observe(document.body, { childList: true, subtree: true });
    },

    /**
     * <hr> and <br> are purely presentational here: the rules and line breaks the theme and its apps sprinkle through
     * body copy carry no meaning of their own. Some screen readers still announce them ("separator", or an extra pause
     * that fragments a sentence), which is noise in the middle of a paragraph.
     *
     * This marks every <hr> and <br> as aria-hidden="true" so they are skipped in the accessibility tree while still
     * rendering visually. Neither element can hold focus or contain content, so nothing is hidden along with them.
     *
     * Content here is injected continuously (apps, quick view, cart drawer, section re-renders), so the observer is
     * never disconnected: it re-runs the pass for the life of the page. It watches childList only, so writing the
     * attribute below does not feed back into it.
     */
    hideDecorativeBreaks() {
      const SELECTOR = 'hr, br';

      const hide = (element) => {
        if (element.dataset.knavBreakHidden) return;
        element.dataset.knavBreakHidden = 'true';
        element.setAttribute('aria-hidden', 'true');
      };

      const scan = (root) => {
        if (!(root instanceof HTMLElement)) return;
        if (root.matches(SELECTOR)) hide(root);
        root.querySelectorAll(SELECTOR).forEach(hide);
      };

      scan(document.body);

      new MutationObserver((records) => {
        for (const record of records) {
          record.addedNodes.forEach(scan);
        }
      }).observe(document.body, { childList: true, subtree: true });
    },

    /**
     * Opt-in list semantics for markup that looks like a list but is not one, or that has lost its semantics to CSS:
     * Safari drops the list role from a <ul> whose list-style is none, and app-built markup often uses stacked <div>s.
     * Either way a screen reader stops announcing "list, 5 items", so users lose both the count and the item-by-item
     * navigation it enables.
     *
     * Add the .knav-list-semantics class to the container and this restores role="list" on it and role="listitem" on
     * its direct children.
     *
     * Direct children only, and deliberately so: role="list" only counts children whose role is listitem, so a wrapper
     * element sitting between the two breaks the relationship. If the markup nests, put the class on the element whose
     * immediate children are the items.
     *
     * Runs once at startup with no observer, so a container injected later is not picked up.
     */
    listSemantics() {
      const SELECTOR = '.knav-list-semantics';

      document.querySelectorAll(SELECTOR).forEach((list) => {
        if (list.dataset.knavListSemantics) return;
        list.dataset.knavListSemantics = 'true';

        list.setAttribute('role', 'list');
        Array.from(list.children).forEach((item) => item.setAttribute('role', 'listitem'));
      });
    },

    /**
     * Form apps render their submit feedback as a plain .message.success / .message.error <div>. Sighted users get the
     * banner appearing under the form; screen reader users get nothing, because focus never moves and the node carries
     * no live-region semantics, so a failed submit reads as if nothing happened.
     *
     * This gives each one role="alert" (implicitly aria-live="assertive" and aria-atomic="true") so its text is
     * announced when it appears. A node that already declares its own role is left alone.
     *
     * These banners arrive after submit, and the apps toggle .success / .error onto an existing .message element as
     * often as they insert a new node, so the observer watches both added nodes and class changes, and is never
     * disconnected. It does not watch the role or data attributes written below, so those cannot feed back into it.
     */
    messageAlerts() {
      const SELECTOR = '.message.success, .message.error';

      const announce = (element) => {
        if (element.dataset.knavAlert) return;
        element.dataset.knavAlert = 'true';

        // Never overwrite a role the app set deliberately.
        if (!element.hasAttribute('role')) element.setAttribute('role', 'alert');
      };

      const scan = (root) => {
        if (!(root instanceof HTMLElement)) return;
        if (root.matches(SELECTOR)) announce(root);
        root.querySelectorAll(SELECTOR).forEach(announce);
      };

      scan(document.body);

      new MutationObserver((records) => {
        for (const record of records) {
          if (record.type === 'attributes') {
            scan(record.target);
          } else {
            record.addedNodes.forEach(scan);
          }
        }
      }).observe(document.body, { childList: true, subtree: true, attributeFilter: ['class'] });
    },

    /**
     * Two patches for the cookie consent app, which renders inside <csm-cookie-consent> and owns an open shadow root.
     *
     * 1. Styles. Page stylesheets do not cross a shadow boundary, so theme.css cannot reach the app's internals no
     *    matter how specific the selector is. The rules below are adopted directly into the root instead.
     *
     * 2. Initial focus. The app opens with focus on the last of its four controls, which drops keyboard users at the
     *    end of the banner with its text behind them. Focus is moved once to the .csm-csb container, made programmatic-
     *    ally focusable with tabindex="-1" so it takes focus without joining the tab order. Focusing the container
     *    rather than a control also lets a screen reader announce the banner's text before its buttons.
     *
     * Polling rather than a MutationObserver, deliberately: the app attaches its shadow root and renders into it after
     * the host lands in the DOM, and neither of those is a document mutation, so a document observer sees the host
     * appear while shadowRoot is still null and then never fires again. Ticks stop once both patches have landed, and
     * TIMEOUT_MS stops them entirely on pages where the banner never appears (already consented, app disabled).
     */
    cookieConsent() {
      const HOST = 'csm-cookie-consent';
      const CONTAINER = '.csm-csb';
      const INTERVAL_MS = 250;
      const TIMEOUT_MS = 20000;

      const CSS = `
        .csm-csb-container:not(:has(.csm-shrinkable-content-expanded)) .csm-csb-buttons,
        .csm-csb-container:not(:has(.csm-shrinkable-content-expanded)) .csm-shrinkable-content {
          visibility: hidden;
        }

        /* The app strips the UA focus ring from its own controls, so keyboard users lose their place inside the
           banner. :focus-visible keeps the ring off mouse clicks; !important beats the app's own reset. */
        *:focus-visible, .csm-dialog-switch input:focus~span.csm-slider {
          outline: .15rem solid rgb(50, 33, 19) !important;
          outline-offset: .1rem !important;
          box-shadow: none !important;
        }

        .csm-csb--shrunk .csm-csb-header > .csm-csb-header-button:focus-visible {
          outline-offset: -0.5rem !important;
          border-radius: 50%;
        }

        .csm-csb-container:has(.csm-shrinkable-content-expanded) .csm-csb-header > .csm-csb-header-button:focus-visible {
          outline-offset: 0.3rem !important;
          border-radius: 0;
        }

        .csm-shrinkable-content:not(.csm-shrinkable-content-expanded) {
          display: none;
        }
        
        .csm-sb-preferences a, .csm-gcm-footer a  {
          text-decoration: underline;
        }
      `;

      // First load only: once focus has been moved, never take it from the user again.
      let focusMoved = false;

      /**
       * Takes the host out of the top layer, back into normal flow, where the z-index theme.css gives it (below the
       * .drawer layer) decides what covers what again. There is no CSS opt-out of the top layer, so the popover
       * attribute itself has to go.
       *
       * The popover methods are stubbed first, because calling either on an element that is no longer a popover
       * throws: the app uses them to reopen the banner from "Your Privacy Choices", and an exception there would
       * leave the banner unreachable. Display is all the attribute was deciding for this host anyway.
       */
      const leaveTopLayer = (host) => {
        if (host.knavTopLayerReleased) return;
        host.knavTopLayerReleased = true;

        host.showPopover = () => host.style.setProperty('display', 'block', 'important');
        host.hidePopover = () => host.style.setProperty('display', 'none', 'important');
        host.togglePopover = (force) => {
          const show = typeof force === 'boolean' ? force : host.style.display === 'none';
          if (show) host.showPopover();
          else host.hidePopover();
          return show;
        };

        host.removeAttribute('popover');

        // The app sets the attribute again when it re-renders the banner, which would return it to the top layer on
        // its next showPopover(). Removing an absent attribute is not a mutation, so this does not feed itself.
        new MutationObserver(() => host.removeAttribute('popover')).observe(host, { attributeFilter: ['popover'] });
      };

      const injectStyles = (root) => {
        if (root.knavStyled) return;
        root.knavStyled = true;

        if ('adoptedStyleSheets' in root && 'replaceSync' in CSSStyleSheet.prototype) {
          const sheet = new CSSStyleSheet();
          sheet.replaceSync(CSS);

          // Append rather than assign, so whatever the app already adopted survives.
          root.adoptedStyleSheets = [...root.adoptedStyleSheets, sheet];
        } else {
          const style = document.createElement('style');
          style.textContent = CSS;
          root.appendChild(style);
        }
      };

      const patch = () => {
        let done = false;

        document.querySelectorAll(HOST).forEach((host) => {
          // Independent of the shadow root: the host is a popover from the moment it lands in the DOM.
          leaveTopLayer(host);

          // null when the root is closed, or when the element has not attached its root yet.
          const root = host.shadowRoot;
          if (!root) return;

          injectStyles(root);

        });

        return done;
      };

      if (patch()) return;

      const timer = setInterval(() => {
        if (patch()) clearInterval(timer);
      }, INTERVAL_MS);

      setTimeout(() => clearInterval(timer), TIMEOUT_MS);
    },


    /**
     * The "Images with text scroll" section (sections/images-with-text-scroll.liquid) renders one
     * .images-with-text-scroll__item per block. Under 1000px those items stop being a scroll-driven narrative and
     * become a swipeable carousel: theme.css hides every item that is not .is-selected, the page dots render, and
     * ImagesWithTextScroll turns swiping on (its `allowSwipe` is `md-max`). Nothing in that markup tells a screen
     * reader that the visible text belongs to one slide out of several, so there is no sense of position or length.
     *
     * This names each item role="group" + aria-label="Slide X of Y" while the carousel is live, and strips both back
     * off at 1000px and up, where the section is a single continuous scroll and "slide" would be a lie.
     *
     * Two notes on the markup:
     *   - The item is display:contents, so it paints no box of its own. Browsers still expose such an element in the
     *     accessibility tree once it carries a role, which is what makes the group land.
     *   - Only the .is-selected item is visible, so exactly one group is exposed at a time — the label is announced as
     *     each slide comes in rather than all of them stacking up.
     */
    imagesWithTextScrollSlides() {
      const SECTION = 'images-with-text-scroll';
      const ITEM = '.images-with-text-scroll__item';

      // Matches the max-width: 999px block in theme.css that turns the section into a carousel.
      const CAROUSEL = window.matchMedia('(max-width: 999px)');

      const label = (index, count) => `Slide ${index} of ${count}`;

      const apply = () => {
        document.querySelectorAll(SECTION).forEach((section) => {
          const items = section.querySelectorAll(ITEM);

          // A single block never carousels — the theme does not even render its dots.
          if (items.length < 2) return;

          items.forEach((item, index) => {
            if (CAROUSEL.matches) {
              item.setAttribute('role', 'group');
              item.setAttribute('aria-label', label(index + 1, items.length));
            } else {
              item.removeAttribute('role');
              item.removeAttribute('aria-label');
            }
          });
        });
      };

      apply();
      CAROUSEL.addEventListener('change', apply);

      // The theme editor replaces a section's markup wholesale on every setting change, which throws these away.
      document.addEventListener('shopify:section:load', apply);
    },

    /**
     * Essential Grid plays its Vimeo videos inline, then leaves .esg-entry-cover sitting over the playing player at
     * exactly the iframe's box, later in the DOM and with pointer-events, so the cover swallows every click and tap
     * aimed at the video. On a pointer device that does not matter: the app's own script fades the cover away on
     * hover, which is how anyone reaches Vimeo's controls. There is no hover on a touch screen, and the app's mobile
     * settings nail the escape hatch shut on top of that:
     *
     *   .overlay-show-mobile { opacity:1!important; visibility:visible!important; transform:unset!important }
     *   .show-mobile-always  { opacity:1!important; visibility:visible!important; transform:unset!important }
     *
     * Both classes are on the live markup, so the cover can never be hidden and Vimeo's controls can never be summoned
     * back once they auto-hide during playback. The video plays on with no way to pause, seek, mute or caption it.
     *
     * This lets pointers through the cover for as long as a video is actually playing, and takes that back the moment
     * it stops so the play button works again. Appearance is deliberately untouched — only whether the cover swallows
     * the tap — because the overlay being visible is a setting the shop chose, while blocking the player is not.
     *
     * Limited to devices with no hover so the desktop behaviour the app ships is left exactly as it is.
     *
     * The rule has to be !important and has to name descendants: the app gives .esg-entry-cover>.eec>* a
     * pointer-events of all, and a descendant that re-enables pointer-events is hit even when an ancestor disabled
     * them, so the play button would otherwise keep eating taps in the middle of the video.
     */
    esgVideoTapThrough() {
      const TOUCH = window.matchMedia('(hover: none)');
      if (!TOUCH.matches) return;

      const ITEM = '.tp-esg-item';
      const ACTIVE = 'esg-video-active';
      const PASS_THROUGH = 'knav-esg-cover-passthrough';
      const STYLE_ID = 'knav-esg-cover-passthrough-style';

      // Two things sit over the player. .esg-entry-cover is the one the app fades on hover, and .lazyloadcover is
      // left behind at z-index 10 with width and height of 100%, which puts it above the iframe as well. Neither has
      // any purpose once the video is running.
      const BLOCKERS = '.esg-entry-cover, .lazyloadcover';

      if (!document.getElementById(STYLE_ID)) {
        const style = document.createElement('style');
        style.id = STYLE_ID;
        style.textContent =
          `.${PASS_THROUGH}, .${PASS_THROUGH} * { pointer-events: none !important; }`;
        document.head.appendChild(style);
      }

      const sync = (item) => {
        if (!(item instanceof HTMLElement)) return;

        const playing = item.classList.contains(ACTIVE);
        item.querySelectorAll(BLOCKERS).forEach((blocker) => blocker.classList.toggle(PASS_THROUGH, playing));
      };

      const scan = (root) => {
        if (!(root instanceof HTMLElement)) return;
        if (root.matches(ITEM)) sync(root);
        root.querySelectorAll(ITEM).forEach(sync);
      };

      scan(document.body);

      // The grid builds itself after this script runs and toggles esg-video-active on the item as videos start and
      // stop, so both the items arriving and their class changing have to be watched, and never disconnected. The
      // class written above lands on the cover, not the item, so it cannot feed back into this.
      new MutationObserver((records) => {
        for (const record of records) {
          if (record.type === 'attributes') scan(record.target);
          else record.addedNodes.forEach(scan);
        }
      }).observe(document.body, { childList: true, subtree: true, attributeFilter: ['class'] });
    }
  };

  const ENABLED = [
    'tingleModal',
    'paymentGateActivation',
    'collectionLayoutSwitch',
    'esgCoverLinks',
    'hideDecorativeBreaks',
    'listSemantics',
    'messageAlerts',
    'cookieConsent',
    'imagesWithTextScrollSlides',
    'esgVideoTapThrough'
  ];

  const run = () => {
    for (const name of ENABLED) {
      try {
        fixes[name]();
      } catch (error) {
        // One broken fix must never take down the rest.
        console.error(`[keyboardnav-fixes] "${name}" failed:`, error);
      }
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run, { once: true });
  } else {
    run();
  }

  function fixContactForm() {
    const formBuilder = document.querySelector('.globo-formbuilder');

    if (!formBuilder) return;

    function removeCheckboxLabelTabindex() {
      formBuilder
        .querySelectorAll(
          'form .checkbox-wrapper .checkbox-label[tabindex]'
        )
        .forEach(label => {
          label.removeAttribute('tabindex');
        });
    }

    // Each field carries its intended token as a class, so the class name is the value: .tel -> autocomplete="tel".
    const AUTOCOMPLETE_TOKENS = ['given-name', 'family-name', 'email', 'tel'];
    const AUTOCOMPLETE_SELECTOR = AUTOCOMPLETE_TOKENS.map((token) => '.' + token).join(', ');

    function addAutocomplete() {
      formBuilder.querySelectorAll(AUTOCOMPLETE_SELECTOR).forEach((element) => {
        // The class sits on the input in this form, but tolerate it landing on the field's wrapper instead.
        const field = element.matches('input, select, textarea')
          ? element
          : element.querySelector('input, select, textarea');

        if (!field || field.hasAttribute('autocomplete')) return;

        const token = AUTOCOMPLETE_TOKENS.find((candidate) => element.classList.contains(candidate));
        if (token) field.setAttribute('autocomplete', token);
      });

      // Fallback for fields that carry no token class. Runs second so the classes above always win.
      formBuilder.querySelectorAll('[data-type="name"], [data-type="email"]').forEach((input) => {
        if (!input.hasAttribute('autocomplete')) {
          if (input.dataset.type === 'name') {
            input.setAttribute('autocomplete', 'name');
          } else if (input.dataset.type === 'email') {
            input.setAttribute('autocomplete', 'email');
          }
        }
      })
    }

    /**
     * The form's accessible name comes from a marker class on the container, so each instance can be named without
     * touching this file: class="globo-formbuilder knav_Bespoke_Inquiry" names the form "Bespoke Inquiry".
     *
     * A <form> is only exposed as a landmark once it has a name, and a page can hold several of these, so the fallback
     * matters as much as the class: an unnamed one is invisible in the landmark list.
     */
    const FORM_LABEL_PREFIX = 'knav_';
    const FALLBACK_FORM_LABEL = 'Contact';

    function formLabel() {
      const marker = Array.from(formBuilder.classList).find((name) => name.startsWith(FORM_LABEL_PREFIX));
      if (!marker) return FALLBACK_FORM_LABEL;

      // "knav_Bespoke_Inquiry" -> "Bespoke Inquiry". A bare "knav_" leaves nothing usable, so fall back there too.
      const label = marker.slice(FORM_LABEL_PREFIX.length).split('_').join(' ').trim();
      return label || FALLBACK_FORM_LABEL;
    }

    function addAccessibleNameToForm() {
      formBuilder.querySelector('form')?.setAttribute('aria-label', formLabel());
    }

    // Check existing content
    removeCheckboxLabelTabindex();
    addAutocomplete();
    addAccessibleNameToForm();

    // Watch only the Globo Form Builder
    const observer = new MutationObserver(() => {
      removeCheckboxLabelTabindex();
      addAutocomplete();
      addAccessibleNameToForm();
    });

    observer.observe(formBuilder, {
      childList: true,
      subtree: true
    });
  }

  fixContactForm();



  /**
   * document.activeElement reports the *host* element when focus sits inside a shadow root, so comparing against it
   * never matches an element in a shadow tree. Walk down through each open shadowRoot to get the element that
   * actually has focus.
   */
  function getActiveElement(root = document) {
    const element = root.activeElement;
    return element && element.shadowRoot
      ? getActiveElement(element.shadowRoot)
      : element;
  }


  /**
   * Reusable focus trap
   *
   * `isEnabled` is an optional predicate, re-read on every Tab, that says whether the trap should hold right now.
   * Containers that stay in the DOM while hidden need it: the cookie banner collapses to a handle rather than
   * unmounting, and a trap that kept holding there would strand keyboard users on an invisible dialog. Returning
   * false lets Tab out untouched, and the trap engages again by itself once the predicate goes back to true.
   *
   * Returns a cleanup function so the trap can be removed later.
   */
  function createFocusTrap(container, isEnabled) {
    if (!container) return function () { };

    const trapHolds = typeof isEnabled === 'function'
      ? isEnabled
      : function () { return true; };

    const focusableSelector = [
      'a[href]',
      'button:not([disabled])',
      'input:not([disabled]):not([type="hidden"])',
      'select:not([disabled])',
      'textarea:not([disabled])',
      '[tabindex]:not([tabindex="-1"])'
    ].join(',');

    /**
     * offsetParent is not a usable visibility test here. The cookie banner hides its collapsed controls with
     * visibility:hidden, which leaves offsetParent non-null and a client rect in place while making the element
     * unfocusable, so the trap would collect controls the browser will never focus: focus() on them is a no-op and
     * the boundary checks below can never match, letting Tab walk straight out of the dialog.
     *
     * Computed visibility inherits, so reading it off the element also catches controls hidden by an ancestor.
     */
    function getFocusableElements() {
      return Array.from(
        container.querySelectorAll(focusableSelector)
      ).filter(function (element) {
        if (
          element.hasAttribute('hidden') ||
          element.getAttribute('aria-hidden') === 'true'
        ) {
          return false;
        }

        // display:none, on the element or any ancestor, leaves no client rects.
        if (!element.getClientRects().length) return false;

        return getComputedStyle(element).visibility !== 'hidden';
      });
    }

    function handleKeydown(event) {
      if (event.key !== 'Tab') return;

      // Dormant: leave the event alone so focus can walk out of the container as it normally would.
      if (!trapHolds()) return;

      const focusableElements = getFocusableElements();

      if (!focusableElements.length) {
        event.preventDefault();
        container.focus();
        return;
      }

      const firstElement = focusableElements[0];
      const lastElement =
        focusableElements[focusableElements.length - 1];

      const activeElement = getActiveElement();
      if (
        event.shiftKey &&
        activeElement === firstElement
      ) {
        event.preventDefault();
        lastElement.focus();
      } else if (
        !event.shiftKey &&
        activeElement === lastElement
      ) {
        event.preventDefault();
        firstElement.focus();
      }
    }

    container.addEventListener('keydown', handleKeydown);

    // Return cleanup method
    return function removeFocusTrap() {
      container.removeEventListener(
        'keydown',
        handleKeydown
      );
    };
  }


  /**
   * Focus trap for the theme's newsletter pop-in.
   */
  function trapNewsletterPopup() {
    const POPUP = '.pop-in.newsletter-popup';

    const wire = (popup) => {
      if (popup.dataset.knavFocusTrap) return;
      popup.dataset.knavFocusTrap = 'true';

      createFocusTrap(popup, () => popup.hasAttribute('open'));
    };

    const wireAll = () => document.querySelectorAll(POPUP).forEach(wire);

    new MutationObserver(wireAll).observe(document.body, {
      childList: true,
      subtree: true
    });

    // The section is rendered server-side, so on a normal page load it is already there on this first pass.
    wireAll();
  }

  trapNewsletterPopup();


  /**
   * Setup Globo dialog
   */
  function setupGloboDialog(trigger) {
    const dialog = document.querySelector(
      '.globo-form-publish-modal.popup'
    );

    if (!dialog) return;

    // Dialog semantics
    dialog.setAttribute('role', 'dialog');
    dialog.setAttribute('aria-modal', 'true');
    dialog.setAttribute(
      'aria-label',
      'Join the private release'
    );

    // Allow dialog itself to receive focus if needed
    if (!dialog.hasAttribute('tabindex')) {
      dialog.setAttribute('tabindex', '-1');
    }

    dialog.querySelector('form.g-container')?.setAttribute('role', 'none');

    dialog.querySelector('.gfb-with-image-img')?.setAttribute('alt', '');

    const autocomplete = ['given-name', 'email', 'address-level1'];
    // Types that cannot carry the tokens above, so they must not shift the count.
    const NOT_A_FIELD = 'input[type="hidden"], input[type="checkbox"], input[type="radio"], input[type="file"]';
    const NOT_A_FIELD_EITHER = 'input[type="submit"], input[type="button"], input[type="reset"], input[type="image"]';

    const addAutocomplete = (container) => {
      if (autocomplete.length === 0) return;

      const fields = Array.from(container.querySelectorAll('input')).filter(
        (input) => !input.matches(NOT_A_FIELD) && !input.matches(NOT_A_FIELD_EITHER)
      );

      autocomplete.forEach((token, index) => {
        const field = fields[index];
        if (field && !field.hasAttribute('autocomplete')) field.setAttribute('autocomplete', token);
      });
    };

    addAutocomplete(dialog);


    /**
     * Close / dismiss control
     */
    const dismiss = dialog.querySelector('.dismiss');

    if (dismiss) {
      dismiss.setAttribute('role', 'button');
      dismiss.setAttribute('tabindex', '0');
      dismiss.setAttribute('aria-label', 'Close');

      // Keyboard activation for custom button
      dismiss.addEventListener('keydown', function (event) {
        if (
          event.key === 'Enter' ||
          event.key === ' '
        ) {
          event.preventDefault();
          dismiss.click();
        }
      });
    }


    /**
     * Create focus trap
     */
    const removeFocusTrap = createFocusTrap(dialog);

    if (dismiss) {
      dismiss.focus();
    } else {
      dialog.focus();
    }

    if (dismiss) {
      dismiss.addEventListener(
        'click',
        function handleClose() {

          removeFocusTrap();

          setTimeout(function () {
            if (trigger) {
              trigger.focus();
            }
          }, 0);

        },
        { once: true }
      );
    }
  }


  /**
   * Listen globally because there may be multiple
   * Globo form triggers on the page.
   */
  document.addEventListener('click', function (event) {

    const trigger = event.target.closest(
      '.globo-formbuilder-open'
    );

    if (!trigger) return;

    // Wait for Globo to create/show the modal
    setTimeout(function () {
      setupGloboDialog(trigger);
    }, 700);

  });


  new MutationObserver(() => {
    const host = document.querySelector('csm-cookie-consent');
    if (!host || !host.shadowRoot) return;

    // The app marks the banner with this while it is collapsed to its handle.
    const SHRUNK = 'csm-csb--shrunk';


    function setupCookieConsentDialog() {

      const dialog = host.shadowRoot.querySelector('.csm-csb');

      if (!dialog) return;

      dialog.querySelector('#isense-cc-cookie-bar-text')?.removeAttribute('tabindex');

      dialog.querySelectorAll('.csm-sb-preferences-category-header').forEach(item => {
        item.querySelector('label > button')?.removeAttribute('aria-label');
        item.querySelector('label > input[type="checkbox"]')?.removeAttribute('aria-labelledby');
      });



      if (dialog.dataset.knavFocusTrap) return;
      dialog.dataset.knavFocusTrap = 'true';


      // Dialog semantics
      dialog.setAttribute('role', 'dialog');
      dialog.setAttribute('aria-modal', 'true');

      // Remove live region behavior
      dialog.removeAttribute('aria-live');

      // Allow fallback focus on the dialog itself
      if (!dialog.hasAttribute('tabindex')) {
        dialog.setAttribute('tabindex', '-1');
      }

      const isShrunk = () => dialog.classList.contains(SHRUNK);

      const removeFocusTrap = createFocusTrap(dialog, () => !isShrunk());

      /**
       * Dismissing the banner from .csm-csb-buttons hands focus to the newsletter pop-in, the thing that takes over the
       * screen next. Without this focus is left on a control that is about to be removed and falls back to the top of
       * the document, putting a keyboard user behind where they started.
       *
       * Delegated from the dialog so it covers whichever controls the app renders in that container, and bound before
       * the shrunk check below so a banner that starts collapsed still gets it. A click listener covers keyboard
       * activation too: Enter and Space on a <button> both dispatch click.
       */
      dialog.addEventListener('click', (event) => {
        if (!event.target.classList.contains('cc-deny') && !event.target.classList.contains('cc-allow')) return;
        const popup = document.querySelector('newsletter-popup');

        // Nothing to hand off to: the section is skipped on non-index templates and for logged-in customers.
        if (!popup) return;

        // The pop-in is not in the tab order, so it needs this to take focus programmatically.
        popup.setAttribute('tabindex', '-1');
        popup.focus();
      });

      // Same reason: never pull focus into a banner that is currently collapsed.
      if (isShrunk()) return removeFocusTrap;

      // Move focus inside
      const firstFocusable = dialog.querySelector('.csm-csb-header-button');

      firstFocusable?.focus();

      return removeFocusTrap;
    }

    if (!host.shadowRoot.knavDialogObserver) {
      const dialogObserver = new MutationObserver(() => setupCookieConsentDialog());

      dialogObserver.observe(host.shadowRoot, { childList: true, subtree: true });
      host.shadowRoot.knavDialogObserver = dialogObserver;
    }

    setupCookieConsentDialog();

  }).observe(document.body, { childList: true, subtree: true });
})();
